import { Dispatch, SetStateAction } from "react";

import { TableColumn } from "@/components/Table/Table";

import { SortParams } from "@/types/types";

import { AssignStatus, InclusionFilterStatus, ListSourceType } from "@/enum/list.enum";

import {
	renderAboutColumn,
	renderAssignToColumn,
	renderCompanyCityColumn,
	renderCompanyColumn,
	renderCompanyCountryColumn,
	renderCompanyPeopleCheckColumn,
	renderCompanyStateColumn,
	renderConnectionsColumn,
	renderEstimatedNumEmployeesColumn,
	renderFollowers,
	renderFoundedYearColumn,
	renderGenderColumn,
	renderHeadlineColumn,
	renderIndustryColumn,
	renderNormalizedCompanyNameColumn,
	renderLanguageColumn,
	renderNameColumn,
	renderPersonCityColumn,
	renderPersonCountryColumn,
	renderPersonStateColumn,
	renderSelectColumn,
	renderSeniorityColumn,
	renderSourceNameColumn,
	renderTitleColumn,
	renderCreatedAt,
	renderEnrichedAtColumn,
	renderSalutationDeColumn,
	renderSalutationEnColumn,
	renderExclusionReasonColumn,
	renderPreviousEmployersColumn,
	renderPersonaCategoryColumn,
	renderCompanyCategoryColumn,
	renderPersonaScoreColumn,
	renderCompanyScoreColumn,
	renderReportetEmployeesColumn,
	renderRoleChangeColumn,
	renderPreviousJobTitleColumn,
	renderPreviousEmployerColumn,
	renderCampaignAddedAt,
	renderAssignedStatusColumn,
	renderInboxOutboxColumn,
	renderLastNodeTypeColumn,
	renderLastScheduledAtColumn,
	renderLastMessageColumn,
	renderTrackerSignalColumn,
	renderTrackerPersonsColumn,
	renderDeepResearchStatusColumn,
	renderSignalScoreColumn,
} from "../../utils/allColumns";
import {
	renderCompanyOtherListsColumn,
	renderOtherListsColumn,
} from "../../utils/otherListsColumns";
import {
	BLACKLIST_DETAIL_COLUMN_UIDS,
	renderBlacklistDetailColumns,
} from "../../utils/columns/blacklistDetailColumns";
import { renderMaybeColumn } from "../../utils/renderMaybeColumn";

import { GetIrlColumns } from "../CustomerImplementations/IrlColumns";

export interface GeneralTableColumnsProps {
	selectAll: boolean;
	setShowAll: Dispatch<SetStateAction<boolean>>;
	toggleSelectAll: () => void;
	selectedItems: number[];
	handleSelectItem: (id: number) => void;
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
	sortingInbox: SortParams;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	integrations: { [key: string]: boolean | undefined };
	handleSelectCampaignAndProspect: (
		id: number | string,
		listPersonId: number,
		type?: "internal" | "lemlist"
	) => void;
	assignStatus: AssignStatus;
	listType?: ListSourceType;
	/** Current inclusion-status view (Added / Unsure / Not found). Drives the Exclusion reason column. */
	inclusionStatus?: InclusionFilterStatus[];
	onResearchClick?: (row: import("../../schema/list").ListPerson) => void;
	onOpenDetail?: (row: import("../../schema/list").ListPerson) => void;
	/** Reason-column row actions: Enrich anyway / plain Retry (PREC-3536 Phase 5). */
	exclusionReasonActions?: import("../../utils/columns/exclusionReasonColumn").ExclusionReasonColumnActions;
	/**
	 * Resolves a `contacts.` key. The Blacklist bucket's columns reuse the Blacklist tab's wording
	 * rather than duplicating it into the inbox namespace (PREC-3594).
	 */
	tsContacts?: (key: string) => string;
}

/**
 * Data columns shown by default, in order (PREC-3567). Every other data column defaults to hidden.
 * Control columns (select/maybe/assign-to/signal-score), tracker context columns, the Assigned-tab
 * columns, custom/score columns, and IRL columns keep their own default visibility and are not
 * governed by this set. Lead-scoring columns (companyCategory/personaCategory) only exist when the
 * LEAD_SCORING integration is enabled.
 */
const DEFAULT_VISIBLE_COLUMN_UIDS = new Set<string>([
	"name",
	"company",
	"title", // Job title
	"roleChange",
	"connections", // Connected on LinkedIn
	"personLocationCountry", // Personal Country
	"country", // Company Country
	"companyCategory", // Company Category (lead scoring)
	"companyEmployees", // Number of employees
	"reportedEmployees", // Reported employees
	"personaCategory", // Persona Category (lead scoring)
	"otherLists", // Other lists (PREC-4185)
	"createdAt", // Added at
]);

/**
 * Columns whose default visibility is contextual, not governed by the PREC-3567 default set — the
 * control columns, tracker signal/persons columns, and the Assigned-tab columns. These keep the
 * `show` value from their own column factory.
 */
const CONTEXTUAL_COLUMN_UIDS = new Set<string>([
	"select",
	"blacklist", // maybe flag
	"signalScore",
	"lemlist", // assign-to
	"trackerSignal",
	"trackerPersons",
]);

const isUnsureOrNotFoundView = (inclusionStatus?: InclusionFilterStatus[]): boolean =>
	!!inclusionStatus?.some((status) =>
		[
			InclusionFilterStatus.UNSURE,
			InclusionFilterStatus.NOT_FOUND,
			InclusionFilterStatus.BOTH,
		].includes(status)
	);

export const GeneralTableColumns = ({
	selectAll,
	setShowAll,
	toggleSelectAll,
	selectedItems,
	handleSelectItem,
	handleCopyClick,
	ts,
	sortingInbox,
	setShowEdit,
	showEdit,
	integrations,
	handleSelectCampaignAndProspect,
	assignStatus,
	listType,
	inclusionStatus,
	onResearchClick,
	onOpenDetail,
	exclusionReasonActions,
	tsContacts,
}: GeneralTableColumnsProps): TableColumn[] => {
	const isTracker = listType === ListSourceType.TRACKER;
	const hasLeadScoring = !!integrations.LEAD_SCORING;

	// Default column order (PREC-3567): control columns first, then the default-visible data columns
	// in the agreed sequence, then the remaining (hidden-by-default) data columns, with Exclusion
	// reason last. Visibility is applied by `applyDefaultVisibility` below.
	const baseColumns: TableColumn[] = [
		renderSelectColumn(setShowAll, toggleSelectAll, selectAll, selectedItems, handleSelectItem),
		renderMaybeColumn(ts),
		...(isTracker ? [renderSignalScoreColumn()] : []),
		renderAssignToColumn(ts, handleCopyClick, handleSelectCampaignAndProspect),
		renderNameColumn(ts, handleCopyClick, sortingInbox, onOpenDetail),
		renderCompanyColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
		...(isTracker ? [renderTrackerSignalColumn(), renderTrackerPersonsColumn()] : []),
		renderExclusionReasonColumn(ts, sortingInbox, exclusionReasonActions),
		...(tsContacts ? renderBlacklistDetailColumns(tsContacts) : []),
		// Default-visible data columns, in order.
		renderTitleColumn(ts, handleCopyClick, sortingInbox), // Job title
		renderRoleChangeColumn(),
		renderConnectionsColumn(ts, sortingInbox), // Connected on LinkedIn
		renderPersonCountryColumn(ts, handleCopyClick, sortingInbox), // Personal Country
		renderCompanyCountryColumn(ts, handleCopyClick, sortingInbox), // Company Country
		...(hasLeadScoring ? [renderCompanyCategoryColumn(sortingInbox)] : []), // Company Category
		renderEstimatedNumEmployeesColumn(ts, handleCopyClick, sortingInbox), // Number of employees
		renderReportetEmployeesColumn(ts, handleCopyClick, sortingInbox), // Reported employees
		...(hasLeadScoring ? [renderPersonaCategoryColumn(sortingInbox)] : []), // Persona Category
		renderOtherListsColumn(ts, sortingInbox), // Other lists (PREC-4185)
		renderCreatedAt(ts, sortingInbox), // Added at
		renderEnrichedAtColumn(ts, sortingInbox), // Enriched at (PREC-3536)
		// Remaining data columns — hidden by default.
		renderCompanyPeopleCheckColumn(ts),
		renderDeepResearchStatusColumn(onResearchClick),
		renderGenderColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
		renderSalutationDeColumn(ts),
		renderSalutationEnColumn(ts),
		renderHeadlineColumn(ts, handleCopyClick, sortingInbox),
		renderAboutColumn(ts, handleCopyClick, sortingInbox),
		renderSeniorityColumn(ts, handleCopyClick, sortingInbox),
		renderPreviousEmployersColumn(ts),
		renderPreviousEmployerColumn(ts),
		renderPreviousJobTitleColumn(ts),
		renderNormalizedCompanyNameColumn(ts, handleCopyClick, sortingInbox),
		renderIndustryColumn(ts, handleCopyClick, sortingInbox),
		renderFoundedYearColumn(ts, handleCopyClick, sortingInbox),
		renderPersonCityColumn(ts, handleCopyClick, sortingInbox),
		renderPersonStateColumn(ts, handleCopyClick, sortingInbox),
		renderCompanyCityColumn(ts, handleCopyClick, sortingInbox),
		renderCompanyStateColumn(ts, handleCopyClick, sortingInbox),
		renderLanguageColumn(ts, sortingInbox),
		renderFollowers(ts, sortingInbox),
		renderCompanyOtherListsColumn(ts, sortingInbox),
		renderSourceNameColumn(ts, handleCopyClick, sortingInbox),
	];

	const showExclusionReason = isUnsureOrNotFoundView(inclusionStatus);
	// The blacklist columns describe why a lead is blocked, which is only ever the question in the
	// Blacklist bucket — everywhere else they would be four empty columns (PREC-3594).
	const showBlacklistDetail = !!inclusionStatus?.includes(InclusionFilterStatus.BLACKLIST);

	const columns: TableColumn[] = baseColumns.map((column) => {
		const uid = column.uid ?? "";

		if (uid === "exclusionReason") {
			return { ...column, show: showExclusionReason };
		}

		if (BLACKLIST_DETAIL_COLUMN_UIDS.has(uid)) {
			return { ...column, show: showBlacklistDetail };
		}

		if (CONTEXTUAL_COLUMN_UIDS.has(uid)) {
			return column;
		}

		return { ...column, show: DEFAULT_VISIBLE_COLUMN_UIDS.has(uid) };
	});

	if (assignStatus === AssignStatus.ASSIGNED) {
		columns.push(renderCampaignAddedAt(ts, sortingInbox));
		columns.push(renderAssignedStatusColumn(ts, sortingInbox));
		columns.push(renderInboxOutboxColumn(ts, sortingInbox));
		columns.push(renderLastNodeTypeColumn(ts, sortingInbox));
		columns.push(renderLastScheduledAtColumn(ts, sortingInbox));
		columns.push(renderLastMessageColumn(ts));
	}

	if (hasLeadScoring) {
		// Persona/company scores are filtered out of the leads table downstream, but stay registered
		// here (hidden by default) for parity with the persisted config.
		columns.push({ ...renderPersonaScoreColumn(sortingInbox), show: false });
		columns.push({ ...renderCompanyScoreColumn(sortingInbox), show: false });
	}

	if (integrations.IRL_COUNTRIES_SUM) {
		const irlColumns = GetIrlColumns(ts);

		columns.push(...irlColumns);
	}

	return columns;
};
